import type { CheckpointResponseResponse } from '@/api/client'
import type { Workflow } from '@/api/hooks/useWorkflow'

export type Checkpoint = Workflow['checkpoints'][number]
export type CheckpointStep = NonNullable<Checkpoint['steps']>[number]
export type CheckpointQuestion = NonNullable<CheckpointStep['questions']>[number]
export type CheckpointAction = Checkpoint['action']

/** A single answer within a checkpoint submission. */
export type CheckpointAnswer = CheckpointResponseResponse['answers'][number]

/**
 * One question's answer, flattened out of its parent submission document and
 * carrying the parent's context (who/when/which checkpoint). The visibility and
 * already-answered logic below all operate on this per-question shape.
 */
export type FlatAnswer = {
  questionId: string
  questionText?: string | null
  value: string[]
  comment?: string | null
  images: NonNullable<CheckpointAnswer['images']>
  questionType?: CheckpointAnswer['questionType']
  answerScope?: CheckpointAnswer['answerScope']
  contentLink?: CheckpointAnswer['contentLink']
  user: string
  createdAt: string
  checkpointId: string
  checkpointName?: string | null
}

/** Expand interaction documents into per-question rows carrying parent context. */
export const flattenResponses = (docs: CheckpointResponseResponse[]): FlatAnswer[] =>
  docs.flatMap(doc =>
    doc.answers.map(a => ({
      questionId: a.questionId,
      questionText: a.questionText,
      value: a.value ?? [],
      comment: a.comment,
      images: a.images ?? [],
      questionType: a.questionType,
      answerScope: a.answerScope,
      contentLink: a.contentLink,
      user: doc.user,
      createdAt: doc.createdAt,
      checkpointId: doc.checkpoint.checkpointId,
      checkpointName: doc.checkpoint.name,
    })),
  )

export type QuestionWithContext = {
  question: CheckpointQuestion
  stepId: string
}

export const getCheckpointForAction = (
  workflowState: Pick<Workflow, 'checkpoints'> | undefined,
  action: CheckpointAction,
): Checkpoint | undefined => workflowState?.checkpoints?.find(c => c.action === action)

const isTriggerSatisfied = (
  triggeredBy: { question?: string | null; response?: string | null } | undefined,
  answersByQuestionId: Map<string, FlatAnswer[]>,
): boolean => {
  if (!triggeredBy?.question || !triggeredBy?.response) return true
  const matches = answersByQuestionId.get(triggeredBy.question) ?? []
  return matches.some(r => r.value?.includes(triggeredBy.response!))
}

const isWorkOrderTypeAllowed = (
  workOrderTypes?: string[] | null,
  workOrderType?: string | null,
): boolean => !workOrderTypes?.length || (!!workOrderType && workOrderTypes.includes(workOrderType))

const isAlreadyAnswered = (
  question: CheckpointQuestion,
  answersByQuestionId: Map<string, FlatAnswer[]>,
  currentUserId: string,
): boolean => {
  if (question.answerScope === 'per-interaction') return false
  if (!question.id) return false
  const matches = answersByQuestionId.get(question.id) ?? []
  if (question.answerScope === 'global') return matches.length > 0
  if (question.answerScope === 'per-user') return matches.some(r => r.user === currentUserId)
  return false
}

export const getQuestionsToAsk = (
  checkpoint: Checkpoint,
  existingResponses: CheckpointResponseResponse[],
  currentUserId: string,
  options: { additionalTriggerAnswers?: FlatAnswer[]; workOrderType?: string | null } = {},
): QuestionWithContext[] => {
  const { additionalTriggerAnswers = [], workOrderType } = options

  const answersByQuestionId = new Map<string, FlatAnswer[]>()
  for (const r of flattenResponses(existingResponses)) {
    const list = answersByQuestionId.get(r.questionId) ?? []
    list.push(r)
    answersByQuestionId.set(r.questionId, list)
  }

  const triggerAnswersByQuestionId = new Map<string, FlatAnswer[]>(answersByQuestionId)
  for (const r of additionalTriggerAnswers) {
    const list = triggerAnswersByQuestionId.get(r.questionId) ?? []
    list.push(r)
    triggerAnswersByQuestionId.set(r.questionId, list)
  }

  const result: QuestionWithContext[] = []

  for (const step of checkpoint.steps ?? []) {
    if (!isTriggerSatisfied(step.triggeredBy, triggerAnswersByQuestionId)) continue
    const stepId = step.id ?? ''

    for (const question of step.questions ?? []) {
      if (!question.id) continue
      if (!isWorkOrderTypeAllowed(question.workOrderTypes, workOrderType)) continue

      if (question.type === 'group') {
        if (!isTriggerSatisfied(question.triggeredBy, triggerAnswersByQuestionId)) continue
        result.push({ question, stepId })
        for (const child of question.questions ?? []) {
          if (!child.id) continue
          if (!isWorkOrderTypeAllowed(child.workOrderTypes, workOrderType)) continue
          if (isAlreadyAnswered(child, answersByQuestionId, currentUserId)) continue
          result.push({ question: child, stepId })
        }
        continue
      }

      if (!isTriggerSatisfied(question.triggeredBy, triggerAnswersByQuestionId)) continue
      if (isAlreadyAnswered(question, answersByQuestionId, currentUserId)) continue
      result.push({ question, stepId })
    }
  }

  return result
}

export const shouldShowCheckpoint = (
  checkpoint: Checkpoint | undefined,
  existingResponses: CheckpointResponseResponse[],
  currentUserId: string,
  options?: { workOrderType?: string | null },
): boolean => {
  if (!checkpoint) return false
  return getQuestionsToAsk(checkpoint, existingResponses, currentUserId, options).length > 0
}

export const getAllQuestions = (checkpoint: Checkpoint): CheckpointQuestion[] => {
  const out: CheckpointQuestion[] = []
  for (const step of checkpoint.steps ?? []) {
    for (const q of step.questions ?? []) {
      if (!q.id) continue
      out.push(q)
      if (q.type === 'group') {
        for (const child of q.questions ?? []) {
          if (child.id) out.push(child)
        }
      }
    }
  }
  return out
}

export type ExistingAnswer = {
  value: string[]
  comment?: string
  images: NonNullable<CheckpointAnswer['images']>
}

export const buildInitialAnswers = (
  questions: CheckpointQuestion[],
  existingResponses: CheckpointResponseResponse[],
): Map<string, ExistingAnswer> => {
  const latestByQuestionId = new Map<string, FlatAnswer>()
  for (const r of flattenResponses(existingResponses)) {
    const prev = latestByQuestionId.get(r.questionId)
    if (!prev || new Date(r.createdAt) > new Date(prev.createdAt)) {
      latestByQuestionId.set(r.questionId, r)
    }
  }

  const out = new Map<string, ExistingAnswer>()
  for (const q of questions) {
    if (!q.id) continue
    const latest = latestByQuestionId.get(q.id)
    if (!latest) continue
    out.set(q.id, {
      value: latest.value ?? [],
      comment: latest.comment ?? undefined,
      images: latest.images ?? [],
    })
  }
  return out
}
